import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  ErrorResponse,
  RotateWebhookSecretRequest,
  TenantWebhookFeature,
  UpdateTenantWebhookFeature,
  UpdateWebhookSettingsRequest,
} from '@clientwire/api';

@Injectable({ providedIn: 'root' })
export class WebhooksService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  readonly loading = signal(false);
  readonly webhookEnabled = signal(false);
  readonly endpointUrl: WritableSignal<string | null> = signal(null);
  readonly secret: WritableSignal<string | null> = signal(null);
  readonly secretVisible = signal(false);
  readonly testingSent = signal(false);

  // Loading states for individual actions
  readonly rotatingSecret = signal(false);
  readonly testingWebhook = signal(false);
  readonly updatingEndpoint = signal(false);

  // Webhook features
  readonly availableWebhookTypes = signal<string[]>([]);
  readonly loadingWebhookFeatures = signal(false);
  readonly webhookFeatures = signal<TenantWebhookFeature[]>([]);
  readonly updatingWebhookFeature = signal(false);

  loadWebhookSettings(): void {
    this.loading.set(true);
    this.#wireApiService.webhooksApi
      .getWebhookSettings()
      .then((response) => {
        this.endpointUrl.set(response.endpointUrl || null);
        this.secret.set(response.secret || null);
        this.webhookEnabled.set(!!response.endpointUrl);
        this.loadWebhookFeatures();
      })
      .catch(async (error) => {
        console.log('Webhook settings error:', error);

        // Check if error is a 404, which indicates no webhook settings yet
        // Handle different possible error structures
        if (
          error.status === 404 ||
          (error.response && error.response.status === 404) ||
          error.statusCode === 404
        ) {
          console.log('Handling 404 for webhook settings as expected case');
          // Handle as a normal case - no webhook configured yet
          this.endpointUrl.set(null);
          this.secret.set(null);
          this.webhookEnabled.set(false);
        } else {
          // Handle other errors normally
          this.handleError('ERROR_LOADING')(error);
          this.rotatingSecret.set(false);
          this.testingWebhook.set(false);
          this.updatingEndpoint.set(false);
        }
      })
      .finally(() => {
        this.loading.set(false);
        // Always load available webhook types, regardless of whether webhooks are enabled
        this.loadAvailableWebhookTypes();
      });
  }

  rotateSecret(): void {
    this.rotatingSecret.set(true);

    const rotateWebhookSecretRequest: RotateWebhookSecretRequest = {};

    this.#wireApiService.webhooksApi
      .rotateWebhooksSecret({ rotateWebhookSecretRequest })
      .then((response) => {
        if (response.secret) {
          this.secret.set(response.secret);
          this.secretVisible.set(true); // Show the new secret automatically
        }
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WEBHOOKS.SECRET_ROTATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WEBHOOKS.SECRET_ROTATED_TITLE')
        );
      })
      .catch(this.handleError('ERROR_ROTATING'))
      .finally(() => {
        this.rotatingSecret.set(false);
      });
  }

  testWebhook(): void {
    this.testingWebhook.set(true);
    this.testingSent.set(false);

    this.#wireApiService.webhooksApi
      .triggerTestWebhook()
      .then(() => {
        this.testingSent.set(true);
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WEBHOOKS.TEST_SENT_MESSAGE'),
          this.#translateService.instant('SETTINGS.WEBHOOKS.TEST_SENT_TITLE')
        );
      })
      .catch(this.handleError('ERROR_TESTING'))
      .finally(() => {
        this.testingWebhook.set(false);
      });
  }

  private handleError(errorType: string) {
    return async (error: any) => {
      console.error(`Error in webhook operation (${errorType}):`, error);

      let errorMessage = this.#translateService.instant(`SETTINGS.WEBHOOKS.${errorType}_MESSAGE`);
      let errorTitle: string; // Default fallback

      // Use specific title translations if available
      if (errorType === 'ERROR_UPDATING') {
        errorTitle = this.#translateService.instant('SETTINGS.WEBHOOKS.ERROR_UPDATING_TITLE');
      } else if (errorType === 'ERROR_ROTATING') {
        errorTitle = this.#translateService.instant('SETTINGS.WEBHOOKS.ERROR_ROTATING_TITLE');
      } else if (errorType === 'ERROR_TESTING') {
        errorTitle = this.#translateService.instant('SETTINGS.WEBHOOKS.ERROR_TESTING_TITLE');
      } else {
        // For ERROR_LOADING or any other types, use generic Error
        errorTitle = this.#translateService.instant('SETTINGS.GENERAL.ERROR_LOADING_TITLE');
      }

      try {
        if (error.response) {
          const errorData: ErrorResponse = await error.response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }

      this.#toastr.error(errorMessage, errorTitle);
    };
  }

  updateWebhookSettings(endpointUrl: string | null): void {
    this.updatingEndpoint.set(true);
    const isNewWebhook = !this.webhookEnabled();

    const updateWebhookSettingsRequest: UpdateWebhookSettingsRequest = {
      endpointUrl: endpointUrl || '', // Convert null to empty string
    };

    this.#wireApiService.webhooksApi
      .setWebhookSettings({ updateWebhookSettingsRequest })
      .then((response) => {
        this.endpointUrl.set(response.endpointUrl || null);
        this.webhookEnabled.set(!!response.endpointUrl);

        // Only update secret if it's returned in the response AND it's a new webhook
        if (response.secret && isNewWebhook) {
          this.secret.set(response.secret);
          this.secretVisible.set(true); // Show the secret automatically if it's new
        }
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WEBHOOKS.SETTINGS_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WEBHOOKS.SETTINGS_UPDATED_TITLE')
        );

        // Load webhook features after successful update if it's a new webhook
        if (isNewWebhook) {
          this.loadWebhookFeatures();
        }
      })
      .catch(this.handleError('ERROR_UPDATING'))
      .finally(() => {
        this.updatingEndpoint.set(false);
        this.rotatingSecret.set(false);
        this.testingWebhook.set(false);
      });
  }

  toggleSecretVisibility(): void {
    this.secretVisible.set(!this.secretVisible());
  }

  loadAvailableWebhookTypes(): void {
    this.loadingWebhookFeatures.set(true);

    this.#wireApiService.webhookFeaturesApi
      .listAvailableWebhookTypes()
      .then((response: string[]) => {
        this.availableWebhookTypes.set(response);
      })
      .catch(this.handleError('ERROR_LOADING'))
      .finally(() => {
        this.loadingWebhookFeatures.set(false);
      });
  }

  loadWebhookFeatures(): void {
    if (!this.webhookEnabled()) {
      // Still keep any existing features, but don't try to load more
      return;
    }

    this.loadingWebhookFeatures.set(true);

    // Get the tenant's webhook features using the correct method name
    this.#wireApiService.webhookFeaturesApi
      .getWebhookFeatures()
      .then((response: TenantWebhookFeature[]) => {
        if (Array.isArray(response)) {
          this.webhookFeatures.set(response);
        }
      })
      .catch(this.handleError('ERROR_LOADING_FEATURES'))
      .finally(() => {
        this.loadingWebhookFeatures.set(false);
      });
  }

  updateWebhookFeature(webhookType: string, enabled: boolean): void {
    this.updatingWebhookFeature.set(true);

    // Create the update request
    const updateRequest: UpdateTenantWebhookFeature = {
      webhookType,
      enabled,
    };

    // Call the API to update the feature using the correct method name
    this.#wireApiService.webhookFeaturesApi
      .updateWebhookFeature({
        updateTenantWebhookFeature: updateRequest,
      })
      .then((response: TenantWebhookFeature) => {
        // Update the local state
        const features = [...this.webhookFeatures()];
        const existingIndex = features.findIndex((f) => f.webhookType === webhookType);

        if (existingIndex >= 0 && features[existingIndex]) {
          // Make sure we have a valid TenantWebhookFeature object
          features[existingIndex] = {
            webhookType: features[existingIndex].webhookType,
            enabled,
          };
        } else if (response) {
          // If we got a response and don't have the feature locally, add it
          features.push({
            webhookType,
            enabled,
          });
        }

        this.webhookFeatures.set(features);

        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WEBHOOKS.FEATURE_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WEBHOOKS.FEATURE_UPDATED_TITLE')
        );
      })
      .catch(this.handleError('ERROR_UPDATING_FEATURE'))
      .finally(() => {
        this.updatingWebhookFeature.set(false);
      });
  }
}
